type UrlGeneratorInputType = {
  name: string;
  size: number;
  type: string;
};

export type GeneratedS3UrlObjectType = {
  objectKey: string;
  s3Url: string;
};

export type S3FileUploadType = {
  generatedUrlObj: GeneratedS3UrlObjectType;
  file: File;
};

export type UploadResponseType = {
  response: Response;
  objectKey: string;
  s3Url: string;
  file: File;
};

export const getUrlGeneratorInput = (files: File[]): UrlGeneratorInputType[] => {
  return files.map((file) => {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
    };
  });
};

/**
 * Handles uploading an individual file. Should not be a public API and only called from uploadFilesToS3().
 * Note: The file will always attempt to upload once regardless of retries count.
 * @param fileToUpload - The file to upload
 * @param retries - The number of retries. This is in addition to the initial upload.
 *
 */
export const uploadFile = async (
  fileToUpload: S3FileUploadType,
  retries: number,
): Promise<UploadResponseType> => {
  let internalRetries = retries;
  let result: UploadResponseType;

  do {
    result = await fetch(fileToUpload.generatedUrlObj.s3Url, {
      method: "PUT",
      body: fileToUpload.file,
    }).then((response) => {
      return {
        response,
        objectKey: fileToUpload.generatedUrlObj.objectKey,
        s3Url: fileToUpload.generatedUrlObj.s3Url,
        file: fileToUpload.file,
      };
    });

    if (result.response.status === 200) {
      break;
    }
  } while (internalRetries-- > 0);
  return result;
};

/**
 * Makes an async call to upload a file to an S3 bucket at the URL stored within
 * the generatedUrlObj. We generate this URL by using a GQL mutation: generatePresignedUrls.
 * @param filesToUpload - S3FileUploadType which the file, url, and objectKey stored in it
 * @param retryCount - the number of times to retry. Will always attempt 1 upload first.
 *
 * Returns: A promise when resolved contains a response and objectKey for the file saved.
 */
export const uploadFilesToS3 = async (
  filesToUpload: S3FileUploadType[],
  retryCount = 3,
): Promise<UploadResponseType[]> => {
  const results = await Promise.all(filesToUpload.map((f) => uploadFile(f, retryCount)));
  if (results.some((r) => r.response.status !== 200)) {
    return Promise.reject(results);
  }
  return results;
};
